import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { SectionHeading, Input } from '@ets/design-system';

import { useGetTemplatesListQuery, useInstallAllTemplatesMutation } from '../TemplatesApi';

import { FilterContent } from '_common/components';
import { clearFilterIdentity, useFilterSelector } from '_common/components/Filters/FilterSlice';
import { useDispatch, useSelector } from '_common/hooks';

type TemplatesHeadingProps = {
  category: Template['category'];
  search: string;
  onSearchChange: (search: string) => void;
  testId: string;
};

const TemplatesHeading = ({ category, search, onSearchChange, testId }: TemplatesHeadingProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { selectFilterParams } = useFilterSelector();
  const filterParams = useSelector((state) =>
    selectFilterParams(state, state.filters[`templates-${category}`]),
  );

  const [installAll] = useInstallAllTemplatesMutation();

  const { data } = useGetTemplatesListQuery();
  const { outdated, not_installed } = useMemo(
    () => ({
      outdated: data
        ? Object.values(data.extensions)
            .filter((template) => template.status === 'outdated')
            .map((template) => template.id)
        : [],
      not_installed: data
        ? Object.values(data.extensions)
            .filter((template) => template.status === 'not_installed')
            .map((template) => template.id)
        : [],
    }),
    [data],
  );

  useEffect(() => {
    return () => {
      dispatch(clearFilterIdentity({ identity: `templates-${category}`, noFeedback: true }));
    };
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    onSearchChange(e.target.value);

  const handleInstallAll = () => {
    installAll({ idList: not_installed });
  };

  const handleUpdateAll = () => {
    installAll({ idList: outdated, updating: true });
  };

  return (
    <SectionHeading
      size="l"
      sticky
      title={
        category === 'general'
          ? intl.formatMessage({ id: 'TEMPLATES' })
          : category.slice(0, 1).toUpperCase() + category.slice(1)
      }
      secondAction={{
        children: intl.formatMessage({ id: 'INSTALL_ALL' }),
        onClick: handleInstallAll,
        disabled: not_installed.length === 0,
        margin: '0 0 0 1rem',
      }}
      firstAction={{
        children: intl.formatMessage({ id: 'UPDATE_ALL' }),
        onClick: handleUpdateAll,
        disabled: outdated.length === 0,
      }}
      searchBar={
        <Input
          prefix="search"
          size="m"
          placeholder={intl.formatMessage({ id: 'TEMPLATE_SEARCH_PLACEHOLDER' })}
          value={search}
          onChange={handleSearchChange}
          testId="templates-search"
          margin="0  1rem 0 0"
        />
      }
      filterPopoverProps={{ align: 'end' }}
      filterContent={
        <FilterContent
          identity={`templates-${category}`}
          templateStatus={{
            options: [
              { value: 'not_installed', label: 'NOT_INSTALLED' },
              { value: 'installed', label: 'INSTALLED' },
              { value: 'outdated', label: 'OUTDATED' },
            ],
          }}
          persist
        />
      }
      filterTriggerProps={{
        iconProps: {
          icon: filterParams.filter_values?.length > 0 ? 'regular-filters-circle-check' : 'filters',
        },
        iconPrefixActive: filterParams.filter_values?.length > 0 ? 'fak' : 'fas',
        iconPrefixInactive: filterParams.filter_values?.length > 0 ? 'fak' : 'far',
      }}
      testId={testId}
    />
  );
};

export default TemplatesHeading;
